/**
 * YDMWordmark — official YDM logo, contrast-aware.
 *
 * Renders the real "ydm + »» arrow" PNG mark from /public/logo. The variant
 * is picked automatically based on the background per the YDM Brand Guideline
 * 2025 contrast rule (dark bg → yellow/white mark, light bg → black mark).
 *
 * Variants:
 *   - `mark`  — bare logo, no tagline (icon role)
 *   - `full`  — same logo but rendered larger / with sibling text
 *   - `hero`  — large lockup with "MAKE IT UNSTOPPABLE" tagline below
 */

import React from 'react';
import { clsx } from 'clsx';

export type WordmarkBackground = 'dark' | 'light' | 'yellow';
export type WordmarkSize = 'sm' | 'md' | 'lg' | 'xl';

export interface YDMWordmarkProps {
  variant?: 'mark' | 'full' | 'hero';
  /** Background the mark sits on. Picks the right contrast file. */
  bg?: WordmarkBackground;
  /** Force a specific logo file under /logo. Overrides `bg`. */
  src?: string;
  /** Show the "MAKE IT UNSTOPPABLE" tagline (auto on for `hero`). */
  withTagline?: boolean;
  size?: WordmarkSize;
  className?: string;
  // Legacy prop kept for back-compat (PR #17 passed `subtitle="Media Intelligence"`).
  // Ignored — the real logo includes its own tagline; subtitle text should live
  // beside the wordmark in the parent if needed.
  subtitle?: string;
}

const HEIGHT: Record<WordmarkSize, string> = {
  sm: 'h-6',   // 24px — chip / inline
  md: 'h-8',   // 32px — sidebar header
  lg: 'h-12',  // 48px — page hero
  xl: 'h-20',  // 80px — auth screens
};

/**
 * Pick logo file. Decision matrix mirrors public/logo/README.md.
 *   dark bg   + no tagline → ydm-04.png (yellow, transparent)
 *   light bg  + no tagline → ydm-08.png (black, transparent)
 *   yellow bg + no tagline → ydm-08.png (black on yellow)
 *   dark bg   + tagline    → ydm-06.png (yellow + tagline, transparent)
 *   light bg  + tagline    → ydm-07.png (black + tagline, transparent)
 *   yellow bg + tagline    → ydm-05.png (full lockup, black on yellow panel)
 */
const pickLogo = (bg: WordmarkBackground, withTagline: boolean): string => {
  if (withTagline) {
    if (bg === 'dark')   return '/logo/ydm-06.png';
    if (bg === 'yellow') return '/logo/ydm-05.png';
    return '/logo/ydm-07.png';
  }
  if (bg === 'dark')   return '/logo/ydm-04.png';
  if (bg === 'yellow') return '/logo/ydm-08.png';
  return '/logo/ydm-08.png';
};

export const YDMWordmark: React.FC<YDMWordmarkProps> = ({
  variant = 'mark',
  bg = 'light',
  src,
  withTagline,
  size,
  className,
}) => {
  const showTagline = withTagline ?? (variant === 'hero');
  const resolvedSize: WordmarkSize = size ?? (variant === 'hero' ? 'xl' : variant === 'full' ? 'md' : 'md');
  const file = src ?? pickLogo(bg, showTagline);
  const alt = showTagline ? 'YDM — Make It Unstoppable' : 'YDM';

  if (variant === 'hero') {
    return (
      <div className={clsx('flex flex-col items-center', className)}>
        <img src={file} alt={alt} className={clsx(HEIGHT[resolvedSize], 'w-auto')} />
      </div>
    );
  }

  return (
    <img
      src={file}
      alt={alt}
      className={clsx(HEIGHT[resolvedSize], 'w-auto inline-block', className)}
    />
  );
};

export default YDMWordmark;
